import type {
  ConditionsConfig,
  ConditionsConfig_ConditionConfig,
  ConditionsConfig_ConditionsConfigGroup,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { ConditionsLogicOperator } from "@smartsuite/automation-engine-api";
import { Paragraph, Text } from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import type { CandidateStore } from "../../../../../stores/automation/candidate.store";
import type { BranchRef } from "../../../../../stores/automation/branches.store";
import { AddConditionsButton } from "./AddConditionsButton";
import { ConditionItem } from "./ConditionItem";
import { EditAutomationPanelStyles } from "./style";
import type { ActionCandidate } from "../../../../../types/candidate";

type ConditionFields = ActionCandidate["description"]["conditionFields"];

export interface ConditionsEditorProps {
  conditions?: ConditionsConfig;
  conditionFields: ConditionFields;
  candidate: CandidateStore;
  integrations: IntegrationDescription[];
  action?: ActionCandidate;
  title?: string;
  subtitle?: string;
  showRequiredMark?: boolean;
  showRequiredError?: boolean;
  allowDelete: boolean;
  isAllowAdd: boolean;
  onAdd: () => void;
  onChange: (
    condition: ConditionsConfig_ConditionConfig,
    index: number,
  ) => void;
  onDelete: (index: number) => void;
  onSwitchOperator: () => void;
  /** Whether an unset operator surfaces the trigger-submitted error state.
   * Defaults to the historical inference "no action = trigger conditions";
   * the branch panel passes false (see ConditionItem). */
  showTriggerConditionErrors?: boolean;
  /** Forces the Find-Record condition set (no "Changes"/"Any" operators).
   * The Conditional Logic branch panel passes true (spec 3.4). */
  excludeChangesFn?: boolean;
  /** Left-side variant forwarded to each ConditionItem (default "static"). */
  leftSide?: "static" | "reference";
  /** The branch whose conditions these are; required for leftSide "reference". */
  branchRef?: BranchRef;
  /** Reveals each row's "left side not picked yet" error (see ConditionItem).
   * Submit-driven -- the branch panel passes the candidate's submitted flag. */
  showIncompleteError?: boolean;
}

// observer: the AND/OR toggle's value comes straight from `conditions.operator`
// (a prop), and the store toggles it in place. Parents like BranchConditionsForm
// only observe `branch.conditions`, not `.operator`, so without observing here
// the read of `conditions.operator` below, toggling AND/OR would not re-render.
export const ConditionsEditor = observer<ConditionsEditorProps>(
  ({
    conditions,
    conditionFields,
    candidate,
    integrations,
    action,
    title,
    subtitle,
    showRequiredMark,
    showRequiredError,
    allowDelete,
    isAllowAdd,
    onAdd,
    onChange,
    onDelete,
    onSwitchOperator,
    showTriggerConditionErrors,
    excludeChangesFn,
    leftSide,
    branchRef,
    showIncompleteError,
  }) => {
    const renderConditionItem = (
      group: ConditionsConfig_ConditionsConfigGroup,
      index: number,
    ): JSX.Element | null => {
      if (group.group.oneofKind === "condition") {
        return (
          <ConditionItem
            // Key by position + selected field only. The operator
            // (function.oneofKind) is deliberately NOT part of the key: including
            // it remounted the whole row on every operator change, wiping the
            // dropdown's open state mid-interaction (so the operator felt
            // un-editable after the first pick). Operator changes are synced via
            // ConditionItem's condition.function effect, no remount needed.
            key={`${index}-${
              group.group.condition.outputReference?.outputId ??
              group.group.condition.conditionFieldId
            }`}
            isInitial={index === 0}
            operator={
              conditions?.operator ?? ConditionsLogicOperator.OPERATOR_AND
            }
            condition={group.group.condition}
            conditionFields={conditionFields}
            candidate={candidate}
            action={action}
            integrations={integrations}
            onChange={(condition) => {
              onChange(condition, index);
            }}
            onDelete={() => {
              onDelete(index);
            }}
            switchOperator={onSwitchOperator}
            allowDelete={allowDelete}
            showTriggerConditionErrors={showTriggerConditionErrors}
            excludeChangesFn={excludeChangesFn}
            leftSide={leftSide}
            branchRef={branchRef}
            showIncompleteError={showIncompleteError}
          />
        );
      } else {
        return null;
      }
    };

    return (
      <>
        {conditionFields.length > 0 && (
          <EditAutomationPanelStyles>
            {title && (
              <Paragraph
                size="m"
                weight="medium"
                styles={{
                  marginBottom: action !== undefined ? 0 : 8,
                }}
              >
                {title}
                {showRequiredMark === true && (
                  <Text
                    size="s"
                    weight="semibold"
                    color="error"
                    styles={{
                      marginLeft: "2px",
                      verticalAlign: "text-top",
                    }}
                  >
                    *
                  </Text>
                )}
              </Paragraph>
            )}
            {action !== undefined && (
              <Paragraph
                color="secondary"
                align="left"
                styles={{ marginBottom: 8 }}
              >
                {subtitle}
              </Paragraph>
            )}
            {action !== undefined &&
              (conditions?.group ?? []).length > 0 &&
              conditions?.group.map(renderConditionItem)}

            {action === undefined && (conditions?.group ?? []).length === 0 && (
              <Paragraph color="secondary" align="left">
                {subtitle}
              </Paragraph>
            )}
            {action === undefined &&
              (conditions?.group ?? []).length > 0 &&
              conditions?.group.map(renderConditionItem)}

            {showRequiredError === true && (
              <Paragraph color={"error"} size={"xs"}>
                {t("automation-form.this-field-is-required")}
              </Paragraph>
            )}
            <AddConditionsButton onClick={onAdd} isAllowAdd={isAllowAdd} />
          </EditAutomationPanelStyles>
        )}
      </>
    );
  },
);
ConditionsEditor.displayName = "ConditionsEditor";
